import { useLocation } from '@reach/router';
import { navigate, withPrefix } from 'gatsby';
import React, { Context, createContext, useState, useEffect } from 'react';
import { useQueryParam, StringParam, ArrayParam } from 'use-query-params';

import { GhostTag } from '../models/ghost';
import { PostCategory } from '../utils/site';
import { getCategoryBySlug, getTagsMap, getTagBySlug } from '../utils/search';

export type SearchFilters = {
  query?: string;
  category?: PostCategory;
  tags?: GhostTag[];
};

export const SearchContext: Context<[SearchFilters, any]> = createContext([
  {},
  null,
]);

const SearchContextProvider: React.FC = ({ children }) => {
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({});

  const [query, setQuery] = useQueryParam("query", StringParam);
  const [category, setCategory] = useQueryParam("category", StringParam);
  const [tags, setTags] = useQueryParam("tag", ArrayParam);

  const location = useLocation();

  useEffect(() => {
    // ensure content tags are unique
    setTags([...new Set(tags)]);
  }, [tags]);

  useEffect(() => {
    // auto-navigate to home on filter clear
    if (!query && !category && (!tags || tags.length <= 0) && location.pathname === withPrefix('/search')) {
      navigate("/");
    }

    setSearchFilters({
      query: query!,
      category: getCategoryBySlug(category!)!,
    });

    // normalize tags
    if (tags && tags.length > 0) {
      const promises = Array.from(tags).map(slug => {
        return getTagBySlug(slug!);
      });
      Promise.all(promises).then(tags => {
        const filteredTags = tags.filter(tag => tag !== null) as GhostTag[];
        setSearchFilters(current => ({
          ...current,
          tags: filteredTags
        }));
      })
    }
  }, [query, category, tags]);

  const setParam = (key: "query" | "category" | "tags", value: string | Array<string>) => {
    switch (key) {
      case "query":
        setQuery(value as string);
        break;
      case "category":
        setCategory(value as string);
        break;
      case "tags":
        setTags(value as Array<string>);
        break;
      default:
        break;
    }
  };

  return (
    <SearchContext.Provider value={[searchFilters, setParam]}>
      {children}
    </SearchContext.Provider>
  );
};

export default SearchContextProvider;
